import React from 'react';

export default function SummaryShareModal({ open, onClose, data }) {
  const ref = React.useRef(null);

  const loadScriptOnce = (url, id) => new Promise((resolve, reject) => {
    if (id && document.getElementById(id)) return resolve(true);
    const s = document.createElement('script');
    if (id) s.id = id;
    s.src = url;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = reject;
    document.body.appendChild(s);
  });

  const generatePdfBlob = async () => {
    const el = ref.current;
    if (!el) return null;
    await loadScriptOnce('https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js', 'tp-html2canvas');
    await loadScriptOnce('https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js', 'tp-jspdf');
    try { if (document?.fonts?.ready) await document.fonts.ready; } catch {}
    const html2canvas = (window || {}).html2canvas;
    const { jsPDF } = (window || {}).jspdf || {};
    if (!html2canvas || !jsPDF) return null;
    const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
    const pdf = new jsPDF('p', 'pt', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 24;
    const maxW = pageWidth - margin * 2;
    const maxH = pageHeight - margin * 2;
    const imgW = canvas.width;
    const imgH = canvas.height;
    const ratio = Math.min(maxW / imgW, 1);
    const scaledW = imgW * ratio;
    const scaledH = imgH * ratio;

    let y = 0;
    let first = true;
    while (y < scaledH - 0.5) {
      const sliceH = Math.min(maxH, scaledH - y);
      const srcSliceH = sliceH / ratio;
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = Math.max(1, Math.floor(scaledW));
      pageCanvas.height = Math.max(1, Math.floor(sliceH));
      const ctx = pageCanvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(
          canvas,
          0,
          Math.floor(y / ratio),
          imgW,
          Math.floor(srcSliceH),
          0,
          0,
          pageCanvas.width,
          pageCanvas.height
        );
      }
      const imgData = pageCanvas.toDataURL('image/jpeg', 0.95);
      if (!first) pdf.addPage();
      pdf.addImage(imgData, 'JPEG', margin, margin, pageCanvas.width, pageCanvas.height, undefined, 'FAST');
      first = false;
      y += sliceH;
    }
    return pdf.output('blob');
  };

  const sharePdf = async () => {
    try {
      const blob = await generatePdfBlob();
      if (!blob) return;
      const filename = `trackprofit-summary-${data?.date}.pdf`;
      const file = new File([blob], filename, { type: 'application/pdf' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'TrackProfit • Today\'s Summary' });
      } else {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { try { URL.revokeObjectURL(a.href); document.body.removeChild(a); } catch {} }, 500);
      }
    } catch {}
  };

  const printOnly = () => {
    try {
      const preview = ref.current;
      if (!preview) { window.print(); return; }
      const clone = preview.cloneNode(true);
      const wrap = document.createElement('div');
      wrap.id = 'tp-print-root';
      wrap.style.background = '#fff';
      wrap.appendChild(clone);
      document.body.appendChild(wrap);
      const style = document.createElement('style');
      style.textContent = `@page { size: A4; margin: 12mm; } @media print { body > *:not(#tp-print-root) { display: none !important; } }`;
      document.head.appendChild(style);
      window.print();
      setTimeout(()=>{ try { document.head.removeChild(style); document.body.removeChild(wrap); } catch {} }, 300);
    } catch {}
  };

  React.useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  if (!open) return null;

  const dayWise = Array.isArray(data?.dayWise) ? data.dayWise : [];
  const totals = data?.totals || { sales: 0, orderCost: 0, expensesDaily: 0, profit: 0 };
  const isWeek = String(data?.timeframe || 'day') === 'week';

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center p-0 sm:p-4">
      <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[95vh] mt-0 sm:mt-4 flex flex-col">
        <div className="p-4 border-b border-green-100 flex items-center justify-between">
          <div className="font-semibold text-green-700">{isWeek ? "Share Weekly Summary" : "Share Today's Summary"}</div>
          <button className="text-sm text-gray-500" onClick={onClose}>✕</button>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm">
            <div ref={ref} className="bg-white border border-green-100 rounded-lg p-3">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded bg-green-100 text-green-700 font-bold flex items-center justify-center">TP</div>
                <div>
                  <div className="font-semibold">TrackProfit</div>
                  <div className="text-[11px] text-gray-500">{isWeek ? 'Weekly Performance Summary' : 'Daily Performance Summary'}</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-[11px] text-gray-500">Date</div>
                  <div className="font-semibold">{data?.date}</div>
                </div>
              </div>
              <div className="my-3 h-px bg-green-100" />
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-green-50 border border-green-100 rounded p-2">
                  <div className="text-[11px] text-gray-500">Sales</div>
                  <div className="font-semibold text-xs break-words">₹{Number(totals.sales||0).toLocaleString('en-IN')}</div>
                </div>
                <div className="bg-green-50 border border-green-100 rounded p-2">
                  <div className="text-[11px] text-gray-500">Cost</div>
                  <div className="font-semibold text-xs break-words">₹{Number((totals.orderCost||0) + (totals.expensesDaily||0)).toLocaleString('en-IN')}</div>
                </div>
                <div className={`bg-green-50 border border-green-100 rounded p-2`}>
                  <div className="text-[11px] text-gray-500">Profit</div>
                  <div className={`font-semibold text-xs break-words ${(Number(totals.profit||0))>=0?'text-green-700':'text-red-600'}`}>₹{Number(totals.profit||0).toLocaleString('en-IN')}</div>
                </div>
              </div>
              <div className="my-3 h-px bg-green-100" />
              <div className="font-semibold mb-2">Daily Breakdown (last 7 days)</div>
              <div className="grid grid-cols-4 gap-2 text-[11px] text-gray-600 font-semibold mb-1">
                <div>Date</div>
                <div className="text-right">Sales</div>
                <div className="text-right">Expenses</div>
                <div className="text-right">Profit</div>
              </div>
              <div className="space-y-1 text-[13px]">
                {dayWise.length === 0 && <div className="text-gray-500">No data</div>}
                {dayWise.map((d, idx) => (
                  <div key={idx} className="grid grid-cols-4 gap-2 items-center">
                    <div className="text-[11px] text-gray-500">{d.date}</div>
                    <div className="text-right text-[10px] break-words">₹{Number(d.sales||0).toLocaleString('en-IN')}</div>
                    <div className="text-right text-[10px] text-gray-600 break-words">₹{Number((d.orderCost||0)+(d.expensesDaily||0)).toLocaleString('en-IN')}</div>
                    <div className={`text-right text-[10px] break-words ${Number(d.profit||0)>=0?'text-green-700':'text-red-600'}`}>₹{Number(d.profit||0).toLocaleString('en-IN')}</div>
                  </div>
                ))}
              </div>
              <div className="my-3 h-px bg-green-100" />
              <div className="mt-2 bg-green-50 border border-green-100 rounded-lg p-3 text-center">
                <div className="text-[12px] font-semibold text-green-800">Invite & Earn</div>
                <div className="text-[12px] text-green-700 font-semibold">Get 1 month free for every referral</div>
                <div className="text-[11px] text-gray-600">When your friend subscribes, both of you get 1 month free.</div>
              </div>
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-green-100 grid grid-cols-2 gap-2">
          <button className="bg-white border border-green-200 text-green-700 rounded py-2" onClick={sharePdf}>Share PDF</button>
          <button className="bg-green-600 text-white rounded py-2" onClick={printOnly}>Download / Print</button>
        </div>
      </div>
    </div>
  );
}


